import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { COUPLE } from "@/data/wedding";
import { GoldDivider } from "./Ornaments";
import { WEDDING } from "@/constants/testIds";

// Browsers refuse to play audible media until the visitor performs a gesture
// that counts as user activation — and a swipe that scrolls the page does not
// count. Rather than leave guests wondering why the invitation is silent, this
// cover asks for one deliberate press to enter.
//
// The music is started by MusicPlayer's own window-level `click` listener,
// which fires on this button inside the activation window. No wiring needed
// here — keep this a real <button> so the click is genuinely trusted.
// Unmounting is driven by a timer rather than an exit-animation callback: if
// frames ever stall (backgrounded tab, reduced-motion quirk, low-end device),
// a cover that never tears down would hide the whole invitation behind an
// opaque panel. A timer removes it regardless of whether the fade rendered.
const FADE_MS = 900;

export const InvitationCover = ({ onOpen }) => {
  const [open, setOpen] = useState(false);
  const [gone, setGone] = useState(false);

  // Hold the page still underneath, so the cover cannot be scrolled past.
  useEffect(() => {
    if (open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, [open]);

  const enter = () => {
    setOpen(true);
    onOpen?.();
    setTimeout(() => setGone(true), FADE_MS);
  };

  if (gone) return null;

  return (
    <motion.div
      animate={{ opacity: open ? 0 : 1, scale: open ? 1.06 : 1 }}
      transition={{ duration: FADE_MS / 1000, ease: [0.22, 1, 0.36, 1] }}
      style={{ pointerEvents: open ? "none" : "auto" }}
      aria-hidden={open}
      className="grain fixed inset-0 z-[100] flex flex-col items-center justify-center bg-[radial-gradient(ellipse_at_center,#ffffff_0%,#fdfbf7_45%,#f4ead2_100%)] px-6 text-center"
    >
      <motion.p
        initial={{ opacity: 0, y: 16 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.9, delay: 0.15 }}
        className="font-malayalam text-xl font-semibold text-[var(--maroon)] md:text-2xl"
      >
        {COUPLE.title}
      </motion.p>

      <motion.div
        initial={{ opacity: 0, y: 16 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.9, delay: 0.3 }}
        className="mt-7 flex flex-col items-center leading-none text-[var(--ink)]"
      >
        <span className="font-display text-5xl italic sm:text-6xl">
          {COUPLE.bride.name}
        </span>
        <span className="font-script my-1 text-4xl text-[var(--gold)] sm:text-5xl">
          &amp;
        </span>
        <span className="font-display text-5xl italic sm:text-6xl">
          {COUPLE.groom.name}
        </span>
      </motion.div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.9, delay: 0.5 }}
        className="mt-8 flex flex-col items-center"
      >
        <GoldDivider />
        <p className="mt-5 font-display text-lg tracking-wide text-[var(--muted)] md:text-xl">
          {COUPLE.savingDate}
        </p>
      </motion.div>

      <motion.button
        data-testid={WEDDING.openInvitation}
        onClick={enter}
        initial={{ opacity: 0, y: 16 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.9, delay: 0.7 }}
        whileTap={{ scale: 0.96 }}
        className="mt-12 rounded-full bg-[var(--maroon)] px-10 py-4 font-body text-xs uppercase tracking-[0.28em] text-white shadow-[0_18px_40px_-14px_rgba(128,0,0,0.7)] transition-transform duration-300 hover:scale-[1.03]"
      >
        Open Invitation
      </motion.button>

      <motion.p
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.9, delay: 0.9 }}
        className="mt-5 font-body text-[10px] uppercase tracking-[0.3em] text-[var(--gold-deep)]"
      >
        Plays music
      </motion.p>
    </motion.div>
  );
};
